import { useAuth } from '@chainlit/react-client';

import signalsMark from '@/assets/signals-node-mark.svg';

import { useIsMobile } from '@/hooks/use-mobile';

import AccountMenu from './AccountMenu';
import NoticesBell from './NoticesBell';
import { isPlainClick, useSections } from './sections';

/**
 * Desktop: the sections as a slim rail down the left edge, in the history
 * sidebar's own colours, one icon and a short label each, with the notification bell and the account menu at the
 * bottom. It replaced the segmented control in the header on 2026-11-04:
 * that control had no room left for a fifth section and read badly, and
 * the rail grows downwards as screens are added.
 *
 * The rail is fixed to the viewport. Everything else moves right by
 * --sig-rail-w (index.css, set only while #sig-rail is in the page and the
 * viewport is md or wider): the pages through the Shell wrapper in
 * router.tsx, and the chat history sidebar, which is fixed too, through its
 * own `left` (ui/sidebar.tsx). Phones never see it: MobileTabBar carries the
 * sections there, and the bell and avatar stay in the header.
 */
export default function Rail() {
  const { isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const nav = useSections();
  if (!isAuthenticated || isMobile) return null;
  const { sections, active, go, hrefOf } = nav;
  if (sections.length < 2) return null;

  return (
    <nav
      id="sig-rail"
      aria-label="Signals sections"
      className="fixed inset-y-0 left-0 z-20 hidden w-[5.5rem] flex-col items-stretch gap-1 border-r bg-sidebar px-2 py-3 text-muted-foreground md:flex"
    >
      <a
        href="/"
        aria-label="Signals"
        className="mx-auto mb-2 grid size-10 place-items-center rounded-xl"
        onClick={(e) => {
          if (!isPlainClick(e)) return;
          e.preventDefault();
          const chat = sections.find((s) => s.key === 'chat');
          if (chat) go(chat);
        }}
      >
        <img src={signalsMark} alt="" className="size-7" aria-hidden="true" />
      </a>
      {sections.map((s) => {
        const on = s.key === active;
        const Icon = s.icon;
        return (
          <a
            key={s.key}
            href={hrefOf(s)}
            aria-current={on ? 'page' : undefined}
            title={s.label}
            onClick={(e) => {
              if (!isPlainClick(e)) return;
              e.preventDefault();
              go(s);
            }}
            className={
              'relative flex flex-col items-center gap-1 rounded-xl px-1 py-2.5 text-center text-[11px] font-semibold leading-tight transition-colors ' +
              (on
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground')
            }
          >
            <Icon className="size-5" aria-hidden="true" />
            <span className="w-full truncate">{s.shortLabel ?? s.label}</span>
            {s.badge && s.badge > 0 ? (
              <span
                dir="ltr"
                className="absolute right-2 top-1.5 rounded-full px-1.5 text-[10px] font-bold leading-4 text-white"
                style={{ background: 'hsl(var(--destructive))' }}
              >
                {s.badge}
              </span>
            ) : null}
          </a>
        );
      })}
      <div className="mt-auto flex flex-col items-center gap-1">
        <NoticesBell />
        <AccountMenu />
      </div>
    </nav>
  );
}
